import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Shield, LogIn } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { FormCard } from "@/components/FormShell";
import { Label } from "@/components/ui/label";
import { FindoSpinner } from "@/components/FindoSpinner";

export default function SuperadminLogin() {
  const { partner, loading: authLoading, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Si déjà connecté en superadmin, on va directement à la console.
  useEffect(() => {
    if (!authLoading && partner?.is_superadmin) navigate("/superadmin", { replace: true });
  }, [authLoading, partner, navigate]);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const p = await login(email.trim().toLowerCase(), password, undefined, false);
      if (p?.is_superadmin) {
        toast.success("Accès autorisé — console superadmin");
        navigate("/superadmin", { replace: true });
      } else {
        toast.error("Ce compte n'a pas accès à la console superadmin.");
        navigate("/b2b/registry", { replace: true });
      }
    } catch (err) {
      const msg = err.message || "Connexion échouée";
      if (err.status === 429) {
        // Lockout: show the countdown ("Réessayez dans X minute(s)") briefly, then auto-hide.
        toast.warning(msg, { duration: 7000 });
      } else if (/mot de passe incorrect/i.test(msg)) {
        toast.error(msg, {
          action: { label: "Réinitialiser", onClick: () => navigate("/superadmin/forgot") },
          duration: 9000,
        });
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-findo-navy text-white grid place-items-center px-4" data-testid="superadmin-login-page">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center gap-3 mb-8">
          <Logo size={34} withText textClass="text-xl text-white" />
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-bold tracking-wide ring-1 ring-white/15">
            <Shield size={14} /> Console Superadmin
          </span>
        </div>

        <FormCard icon={Shield} title="Connexion administrateur" subtitle="Accès réservé à l'équipe Findloop." className="text-slate-900 shadow-2xl" testid="superadmin-login-card">
          <h1 className="sr-only">Connexion administrateur</h1>
          <form onSubmit={submit} className="space-y-6" data-testid="superadmin-login-form">
            <div>
              <Label htmlFor="sa-email">Email administrateur</Label>
              <Input id="sa-email" type="email" required value={email} autoComplete="username"
                onChange={(e) => setEmail(e.target.value)} placeholder="admin@getfindo.io"
                className="mt-1.5 h-11 rounded-xl"
                data-testid="superadmin-login-email" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="sa-password">Mot de passe</Label>
                <Link to="/superadmin/forgot" className="text-xs font-semibold text-findo-red hover:underline" data-testid="superadmin-forgot-link">
                  Mot de passe oublié ?
                </Link>
              </div>
              <PasswordInput id="sa-password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5 h-11 rounded-xl"
                data-testid="superadmin-login-password" />
            </div>
            <Button type="submit" disabled={loading}
              className="w-full h-11 rounded-xl font-semibold bg-findo-red hover:bg-findo-red/90 text-white"
              data-testid="superadmin-login-submit">
              <LogIn size={16} className="mr-1.5" />
              {loading ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />Connexion…</span> : "Accéder à la console"}
            </Button>
          </form>
        </FormCard>

        <p className="mt-6 text-center text-xs text-white/40">
          <Link to="/" className="hover:text-white/70 transition-colors" data-testid="superadmin-login-back-site">← Retour au site findloop</Link>
        </p>
      </div>
    </div>
  );
}
